import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { assignLawyerLevel, fetchPricingLevels } from "@admin/services/adminApi";
import { LevelHistory } from "@admin/components/lawyers/LevelHistory";

const rupees = (paise) => (paise == null ? "—" : `₹${Math.round(paise / 100).toLocaleString("en-IN")}`);
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export function PricingLevelCard({ lawyerId, level, pending, onChanged }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [choice, setChoice] = useState("");
  const [customFee, setCustomFee] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || cfg) return;
    fetchPricingLevels().then(setCfg).catch(() => toast.error("Could not load pricing levels"));
  }, [open, cfg]);

  const isCustom = Number(choice) === (cfg?.custom_level || 11);
  const canSubmit = choice && (!isCustom || Number(customFee) > 0) && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { level: Number(choice), reason: reason.trim() || undefined };
      if (isCustom) payload.custom_fee_rupees = Number(customFee);
      await assignLawyerLevel(lawyerId, payload);
      toast.success(`Level ${choice} assigned — the lawyer has been emailed`);
      setOpen(false); setChoice(""); setCustomFee(""); setReason("");
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 403 && d?.code === "TWO_FACTOR_REQUIRED") return;
      toast.error(typeof d === "string" ? d : "Could not assign level");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="pricing-level-card">
      {level?.active_level ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <Stat k="Active level" v={<span className="text-lg font-extrabold text-[#0A2540]" data-testid="pricing-level-active">Level {level.active_level}</span>} />
          <Stat k="Base fee (video · 15 min)" v={<span data-testid="pricing-level-fee">{rupees(level.active_base_fee_paise)}</span>} />
          <Stat k="Customer price (incl. GST)" v={rupees(level.customer_base_price_inc_gst_paise)} />
          <Stat k="Assigned" v={<span data-testid="pricing-level-assigned">{when(level.approved_at)}<br /><span className="text-xs text-slate-500">{level.approved_by_email || level.approved_by || "—"}{level.config_version ? ` · config v${level.config_version}` : ""}</span></span>} />
          {level.adjustment_reason && <Stat k="Reason" v={<span className="text-slate-600">{level.adjustment_reason}</span>} />}
        </div>
      ) : (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="pricing-level-none">
          No level assigned yet — the lawyer cannot be verified until an Admin sets one.
        </p>
      )}
      {pending && (
        <p className="mt-3 text-xs text-slate-600" data-testid="pricing-level-pending">
          Pending request: <span className="font-bold">Level {pending.requested_level}</span> ({pending.status}) — decide it under Pricing &amp; Offers, or assign directly below.
        </p>
      )}
      <div className="mt-4">
        <Button size="sm" onClick={() => setOpen(true)} data-testid="pricing-level-assign-btn"
                className="bg-[#0A2540] text-[#D4AF37] hover:bg-[#0A2540]/90 rounded-lg">
          <Layers size={14} className="mr-1" /> {level?.active_level ? "Change level" : "Assign level"}
        </Button>
      </div>

      <LevelHistory lawyerId={lawyerId} refreshKey={level?.approved_at || "none"} />

      <AlertDialog open={open} onOpenChange={(o) => { if (!o && !busy) setOpen(false); }}>
        <AlertDialogContent data-testid="pricing-level-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{level?.active_level ? `Change level (currently Level ${level.active_level})` : "Assign a pricing level"}</AlertDialogTitle>
            <AlertDialogDescription>
              The level sets the lawyer's base consultation fee; customer prices derive from it. The lawyer is emailed about the change. Requires fresh 2FA.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!cfg ? (
            <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading levels…</div>
          ) : (
            <div className="space-y-3">
              <Select value={choice} onValueChange={setChoice}>
                <SelectTrigger data-testid="pricing-level-select"><SelectValue placeholder="Choose a level" /></SelectTrigger>
                <SelectContent>
                  {cfg.levels.map((l) => (
                    <SelectItem key={l.level} value={String(l.level)} data-testid={`pricing-level-option-${l.level}`}>
                      Level {l.level} — ₹{l.base_fee_rupees.toLocaleString("en-IN")}
                    </SelectItem>
                  ))}
                  <SelectItem value={String(cfg.custom_level || 11)} data-testid="pricing-level-option-custom">Level {cfg.custom_level || 11} — custom fee</SelectItem>
                </SelectContent>
              </Select>
              {isCustom && (
                <Input type="number" min={1} value={customFee} onChange={(e) => setCustomFee(e.target.value)}
                       placeholder="Custom base fee in ₹" data-testid="pricing-level-custom-fee" />
              )}
              <Textarea value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))} rows={2}
                        placeholder="Reason (optional, shown in audit and to the lawyer)" data-testid="pricing-level-reason" />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="pricing-level-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); submit(); }} disabled={!canSubmit} data-testid="pricing-level-confirm"
                               className="bg-[#0A2540] text-[#D4AF37] hover:bg-[#0A2540]/90">
              {busy ? <Loader2 size={14} className="animate-spin" /> : "Assign & notify"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Stat = ({ k, v }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{k}</p>
    <div className="mt-0.5 text-sm text-slate-800">{v}</div>
  </div>
);
